import styled, { useTheme } from "styled-components/macro";
import type { SystemMessage, SystemMessageKind } from "../../../../types/types";
import { formatDateTime } from "../../../../util/util";
import { useTranslation } from "react-i18next";
import {
  InfoIcon,
  WarningIcon,
  ArchiveIcon,
  XIcon,
} from "../../../../components/Icons/Icons";

type CardWrapperProps = {
  messageType: SystemMessageKind;
  isAdminView: boolean;
  isInactive: boolean;
};

const CardWrapper = styled.div<CardWrapperProps>`
  background-color: ${({ theme, messageType }) =>
    messageType === "alert"
      ? theme.resolve("color_bg_surface_warning")
      : theme.resolve("color_bg_surface_info")};
  border: 1px solid
    ${({ theme, messageType }) =>
      messageType === "alert"
        ? theme.resolve("color_border_warning")
        : theme.resolve("color_border_info")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  position: relative;

  &[data-inactive="true"] {
    opacity: 0.7;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  align-items: center;
  width: 100%;
`;

const IconAndTitleContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
  flex: 1;
`;

const CreatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  flex: 1;
`;

const IconWrapper = styled.div<{ messageType: SystemMessageKind }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${({ theme, messageType }) =>
    messageType === "alert"
      ? theme.resolve("color_icon_warning")
      : theme.resolve("color_icon_info")};
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  flex: 1;
`;

const MetaInfo = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  line-height: ${({ theme }) => theme.resolve("line_height_xs")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  white-space: nowrap;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
`;

const ActionButton = styled.button`
  background-color: ${({ theme }) =>
    theme.resolve("color_buttonIcon_bg_enabled")};
  border: ${({ theme }) => theme.resolve("border_none")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.resolve("color_buttonIcon_icon_enabled")};
  width: 20px;
  height: 20px;

  &:hover {
    background-color: ${({ theme }) => theme.resolve("color_bg_surface_hover")};
  }
`;

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  width: 100%;
`;

const Content = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  width: 100%;

  p,
  ul,
  ol,
  li,
  strong,
  em,
  u,
  s {
    all: revert;
  }

  && p:first-child {
    margin-block-start: 0;
    margin-top: 0;
  }

  && p:last-child {
    margin-block-end: 0;
    margin-bottom: 0;
  }
`;

const PublishDuration = styled(MetaInfo)``;

type BroadcastCardProps = {
  message: SystemMessage;
  onArchive?: (messageId: string) => void;
  onDismiss?: (messageId: string) => void;
  canArchive?: boolean;
  showActions?: boolean;
  isAdminView?: boolean;
};

export const BroadcastCard = ({
  message,
  onArchive,
  onDismiss,
  canArchive = false,
  showActions = true,
  isAdminView = false,
}: BroadcastCardProps) => {
  const { t } = useTranslation();
  const isInactive = message.state === "archived";
  const theme = useTheme();

  const handleArchiveClick = () => {
    onArchive?.(message.id);
  };

  const handleDismissClick = () => {
    onDismiss?.(message.id);
  };

  const createMarkup = (htmlContent: string) => {
    const sanitizedHtml = htmlContent.replace(/<script.*?>.*?<\/script>/gi, "");
    return { __html: sanitizedHtml };
  };

  return (
    <CardWrapper
      messageType={message.type}
      isAdminView={isAdminView}
      isInactive={isInactive}
      data-inactive={isInactive}
    >
      <HeaderContainer>
        <IconAndTitleContainer>
          <CreatorContainer>
            <IconAndTitleContainer>
              <IconWrapper messageType={message.type}>
                {message.type === "alert" ? (
                  <WarningIcon
                    width={20}
                    height={20}
                    fill={theme.resolve("color_icon_warning")}
                  />
                ) : (
                  <InfoIcon
                    width={20}
                    height={20}
                    fill={theme.resolve("color_icon_info")}
                  />
                )}
              </IconWrapper>
              <Title>{message.header}</Title>
            </IconAndTitleContainer>
            <MetaInfo>
              {message.created_by} {" | "}
              {formatDateTime(message.created_at)}
            </MetaInfo>
          </CreatorContainer>
        </IconAndTitleContainer>

        {showActions && !isInactive && (
          <ActionsWrapper>
            {canArchive ? (
              <>
                {onArchive && (
                  <ActionButton
                    onClick={handleArchiveClick}
                    aria-label={t("Archive broadcast")}
                  >
                    <ArchiveIcon width={20} height={20} />
                  </ActionButton>
                )}
              </>
            ) : (
              <>
                {onDismiss && (
                  <ActionButton
                    onClick={handleDismissClick}
                    aria-label={t("Dismiss message")}
                  >
                    <XIcon width={20} height={20} />
                  </ActionButton>
                )}
              </>
            )}
          </ActionsWrapper>
        )}
      </HeaderContainer>

      <BodyContainer>
        {isAdminView && !isInactive && message.expires_at && (
          <MetaInfo>
            {t("Expires on")}: {formatDateTime(message.expires_at!)}
          </MetaInfo>
        )}

        {isInactive && message.archived_at && (
          <PublishDuration>
            {`${t("Publish duration")}: ${formatDateTime(
              message.created_at
            )} - ${formatDateTime(message.archived_at)}`}
          </PublishDuration>
        )}

        <Content dangerouslySetInnerHTML={createMarkup(message.content)} />
      </BodyContainer>
    </CardWrapper>
  );
};
